import React from "react";
import { t } from "@auto/services";
import { can } from "@src/security/permissions";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";

import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import {
  REPORT_MODES,
  SOURCE_OPTIONS,
} from "@src/reporting/model/reportingConstants.mjs";
import { reportingResourceKey } from "@src/reporting/model/reportingFilters.mjs";
import { labelOf } from "@src/reporting/model/reportingFormatters.mjs";
import {
  loadArrondissements,
  loadCommunes,
  loadConstituencies,
  loadDepartments,
  loadSections,
  loadVotingCenters,
  searchReportingOperators,
} from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const SelectField = ({ label, value, onChange, children, disabled = false }) => (
  <TextField
    select
    size="small"
    label={label}
    value={value ?? ""}
    onChange={onChange}
    disabled={disabled}
    fullWidth
  >
    {children}
  </TextField>
);

const entityOptions = (rows, allLabel) => [
  <MenuItem key="all" value="">{allLabel}</MenuItem>,
  ...(rows || []).map((row) => (
    <MenuItem key={row.gid} value={String(row.gid)}>
      {labelOf(row)}
    </MenuItem>
  )),
];

const OperatorAutocomplete = ({ nationalId, onChange }) => {
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    if (!nationalId) {
      setSelected(null);
      return;
    }
    if (selected?.nationalId === nationalId) return;

    const known = options.find((option) => option.nationalId === nationalId);
    setSelected(known || {
      nationalId,
      name: nationalId,
      usernames: [],
      accountCount: 0,
    });
  }, [nationalId, options, selected]);

  React.useEffect(() => {
    const search = inputValue.trim();
    if (search.length < 2) {
      setOptions(selected ? [selected] : []);
      setLoading(false);
      setError("");
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      searchReportingOperators(search)
        .then((rows) => {
          if (!active) return;
          const next = rows || [];
          if (selected && !next.some((row) => row.nationalId === selected.nationalId)) {
            next.unshift(selected);
          }
          setOptions(next);
        })
        .catch((reason) => {
          if (!active) return;
          setError(reason?.message || String(reason));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [inputValue, selected]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      inputValue={inputValue}
      loading={loading}
      onInputChange={(_, nextValue) => setInputValue(nextValue)}
      onChange={(_, option) => {
        setSelected(option);
        onChange(option?.nationalId || "");
      }}
      isOptionEqualToValue={(option, current) => option.nationalId === current.nationalId}
      getOptionLabel={(option) => option?.name || option?.nationalId || ""}
      filterOptions={(rows) => rows}
      noOptionsText={inputValue.trim().length < 2 ? t("reporting.typeTwoChars") : t("reporting.noOperators")}
      renderOption={(props, option) => (
        <li {...props} key={option.nationalId}>
          <div>
            <div className={styles.autocompletePrimary}>{option.name}</div>
            <div className={styles.autocompleteSecondary}>
              NINU {option.nationalId}
              {option.accountCount ? ` · ${option.accountCount} ${option.accountCount === 1 ? t("common.account") : t("common.accountsLower")}` : ""}
            </div>
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("voter.operator")}
          placeholder={t("reporting.operatorPlaceholder")}
          error={Boolean(error)}
          helperText={error || undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const ReportingFilters = ({
  mode = "advanced",
  reportMode = REPORT_MODES.STANDARD,
  value,
  onChange,
  onApply,
  onReset,
  onReportModeChange,
  gui,
}) => {
  const advanced = mode === "advanced";
  const correction = reportMode === REPORT_MODES.CORRECTION;
  const canSearchOperators = can(gui, "SEARCH_USER_ACCOUNT");

  const roots = useReportingResource(
    async () => {
      const [departments, arrondissements, communes, constituencies] = await Promise.all([
        loadDepartments(),
        loadArrondissements(),
        loadCommunes(),
        loadConstituencies(),
      ]);
      return {
        departments: departments || [],
        arrondissements: arrondissements || [],
        communes: communes || [],
        constituencies: constituencies || [],
      };
    },
    {
      key: "reporting-filter-roots",
      initialData: {
        departments: [],
        arrondissements: [],
        communes: [],
        constituencies: [],
      },
      enabled: advanced,
    },
  );

  const geographyOptions = React.useMemo(() => {
    const departments = roots.data.departments || [];
    const arrondissements = roots.data.arrondissements || [];
    const communes = roots.data.communes || [];
    const constituencies = roots.data.constituencies || [];

    const arrondissementByGid = new Map(
      arrondissements.map((row) => [String(row.gid), row]),
    );

    const same = (left, right) => String(left ?? "") === String(right ?? "");

    const communeMatches = (commune, {
      departmentGid = "",
      arrondissementGid = "",
      constituencyGid = "",
      communeGid = "",
    } = {}) => {
      if (communeGid && !same(commune.gid, communeGid)) return false;
      if (arrondissementGid && !same(commune.arrondissementGid, arrondissementGid)) return false;
      if (constituencyGid && !same(commune.constituencyGid, constituencyGid)) return false;

      if (departmentGid) {
        const arrondissement = arrondissementByGid.get(String(commune.arrondissementGid));
        if (!arrondissement || !same(arrondissement.departmentGid, departmentGid)) return false;
      }

      return true;
    };

    const departmentConstrained = Boolean(
      value.arrondissementGid || value.constituencyGid || value.communeGid,
    );
    const departmentRows = departmentConstrained
      ? departments.filter((department) => communes.some((commune) => {
        const arrondissement = arrondissementByGid.get(String(commune.arrondissementGid));
        return arrondissement
          && same(arrondissement.departmentGid, department.gid)
          && communeMatches(commune, {
            arrondissementGid: value.arrondissementGid,
            constituencyGid: value.constituencyGid,
            communeGid: value.communeGid,
          });
      }))
      : departments;

    const arrondissementConstrained = Boolean(
      value.departmentGid || value.constituencyGid || value.communeGid,
    );
    const arrondissementRows = arrondissementConstrained
      ? arrondissements.filter((arrondissement) => {
        if (value.departmentGid
            && !same(arrondissement.departmentGid, value.departmentGid)) {
          return false;
        }

        if (!value.constituencyGid && !value.communeGid) return true;

        return communes.some((commune) => (
          same(commune.arrondissementGid, arrondissement.gid)
          && communeMatches(commune, {
            constituencyGid: value.constituencyGid,
            communeGid: value.communeGid,
          })
        ));
      })
      : arrondissements;

    const constituencyConstrained = Boolean(
      value.departmentGid || value.arrondissementGid || value.communeGid,
    );
    const constituencyRows = constituencyConstrained
      ? constituencies.filter((constituency) => communes.some((commune) => (
        same(commune.constituencyGid, constituency.gid)
        && communeMatches(commune, {
          departmentGid: value.departmentGid,
          arrondissementGid: value.arrondissementGid,
          communeGid: value.communeGid,
        })
      )))
      : constituencies;

    const communeRows = communes.filter((commune) => communeMatches(commune, {
      departmentGid: value.departmentGid,
      arrondissementGid: value.arrondissementGid,
      constituencyGid: value.constituencyGid,
    }));

    return {
      departments: departmentRows,
      arrondissements: arrondissementRows,
      constituencies: constituencyRows,
      communes: communeRows,
      communeMatches,
    };
  }, [roots.data, value.departmentGid, value.arrondissementGid, value.constituencyGid, value.communeGid]);

  const sections = useReportingResource(
    () => loadSections(value.communeGid),
    {
      key: reportingResourceKey("sections", value.communeGid),
      enabled: advanced && Boolean(value.communeGid),
      initialData: [],
    },
  );

  const votingCenters = useReportingResource(
    () => loadVotingCenters(value.sectionGid),
    {
      key: reportingResourceKey("voting-centers", value.sectionGid),
      enabled: advanced && Boolean(value.sectionGid),
      initialData: [],
    },
  );

  const set = (patch) => onChange({ ...value, ...patch });
  const setField = (field, nextValue) => set({ [field]: nextValue });

  const changeDepartment = (departmentGid) => {
    const constituencyGid = value.constituencyGid
      && departmentGid
      && !roots.data.communes.some((commune) => geographyOptions.communeMatches(commune, {
        departmentGid,
        constituencyGid: value.constituencyGid,
      }))
      ? ""
      : value.constituencyGid;

    set({
      departmentGid,
      arrondissementGid: "",
      constituencyGid,
      communeGid: "",
      sectionGid: "",
      votingCenterGid: "",
    });
  };

  const changeArrondissement = (arrondissementGid) => {
    const constituencyGid = value.constituencyGid
      && arrondissementGid
      && !roots.data.communes.some((commune) => geographyOptions.communeMatches(commune, {
        arrondissementGid,
        constituencyGid: value.constituencyGid,
      }))
      ? ""
      : value.constituencyGid;

    set({
      arrondissementGid,
      constituencyGid,
      communeGid: "",
      sectionGid: "",
      votingCenterGid: "",
    });
  };

  const changeConstituency = (constituencyGid) => {
    let departmentGid = value.departmentGid;
    let arrondissementGid = value.arrondissementGid;

    if (constituencyGid) {
      if (arrondissementGid
          && !roots.data.communes.some((commune) => geographyOptions.communeMatches(commune, {
            arrondissementGid,
            constituencyGid,
          }))) {
        arrondissementGid = "";
      }

      if (departmentGid
          && !roots.data.communes.some((commune) => geographyOptions.communeMatches(commune, {
            departmentGid,
            constituencyGid,
          }))) {
        departmentGid = "";
      }
    }

    set({
      constituencyGid,
      departmentGid,
      arrondissementGid,
      communeGid: "",
      sectionGid: "",
      votingCenterGid: "",
    });
  };

  const changeCommune = (communeGid) => set({
    communeGid,
    sectionGid: "",
    votingCenterGid: "",
  });

  const changeSection = (sectionGid) => set({
    sectionGid,
    votingCenterGid: "",
  });

  const filterError = [
    roots.error,
    sections.error,
    votingCenters.error,
  ].find(Boolean) || "";

  const invalidDateRange = Boolean(value.dateFrom && value.dateTo && value.dateFrom > value.dateTo);

  const loading = advanced && (
    roots.loading
    || sections.loading
    || votingCenters.loading
  );

  const modeAction = (
    <Button
      variant={correction ? "outlined" : "text"}
      startIcon={correction ? <ArrowBackOutlinedIcon /> : <FindInPageOutlinedIcon />}
      onClick={() => onReportModeChange?.(
        correction ? REPORT_MODES.STANDARD : REPORT_MODES.CORRECTION,
      )}
    >
      {correction ? t("reporting.standardRegistration") : t("reporting.correctionReporting")}
    </Button>
  );

  return (
    <section className={`${styles.filtersPanel} ${correction ? styles.correctionFilters : ""}`}>
      {filterError ? <Alert severity="error" className={styles.filterAlert}>{filterError}</Alert> : null}
      {invalidDateRange ? <Alert severity="error" className={styles.filterAlert}>{t("reporting.invalidDateRange")}</Alert> : null}

      <div className={styles.filterSection}>
        {advanced ? <div className={styles.filterSectionTitle}>{t("reporting.generalFilters")}</div> : null}
        <div className={advanced ? styles.filterGrid : styles.filterGridCompact}>
          <TextField
            size="small"
            type="date"
            label={t("common.from")}
            value={value.dateFrom || ""}
            onChange={(event) => setField("dateFrom", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label={t("common.to")}
            value={value.dateTo || ""}
            onChange={(event) => setField("dateTo", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {advanced ? (
            <>
              <SelectField
                label={t("common.source")}
                value={value.source}
                onChange={(event) => setField("source", event.target.value)}
              >
                <MenuItem value="">{t("common.allSources")}</MenuItem>
                {SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={String(option.value)}>{option.label}</MenuItem>
                ))}
              </SelectField>

              {canSearchOperators ? (
                <OperatorAutocomplete
                  nationalId={value.operatorNationalId}
                  onChange={(operatorNationalId) => setField("operatorNationalId", operatorNationalId)}
                />
              ) : (
                <TextField
                  size="small"
                  label={t("voter.operator")}
                  placeholder={t("reporting.operatorPlaceholder")}
                  value={value.operatorNationalId || ""}
                  onChange={(event) => setField("operatorNationalId", event.target.value)}
                  fullWidth
                />
              )}
            </>
          ) : null}
        </div>
      </div>

      {advanced ? (
        <div className={styles.filterSectionGeography}>
          <div className={styles.filterSectionTitle}>{t("reporting.geographicScope")}</div>
          <div className={styles.filterGrid}>
            <SelectField
              label={t("person.department")}
              value={value.departmentGid}
              onChange={(event) => changeDepartment(event.target.value)}
            >
              {entityOptions(geographyOptions.departments, t("reporting.allDepartments"))}
            </SelectField>

            <SelectField
              label={t("geo.arrondissement")}
              value={value.arrondissementGid}
              onChange={(event) => changeArrondissement(event.target.value)}
              disabled={!value.departmentGid && !value.constituencyGid}
            >
              {entityOptions(geographyOptions.arrondissements, t("reporting.allArrondissements"))}
            </SelectField>

            <SelectField
              label={t("geo.constituency")}
              value={value.constituencyGid}
              onChange={(event) => changeConstituency(event.target.value)}
            >
              {entityOptions(geographyOptions.constituencies, t("reporting.allConstituencies"))}
            </SelectField>

            <SelectField
              label={t("person.commune")}
              value={value.communeGid}
              onChange={(event) => changeCommune(event.target.value)}
              disabled={!value.arrondissementGid && !value.constituencyGid}
            >
              {entityOptions(geographyOptions.communes, t("reporting.allCommunes"))}
            </SelectField>

            <SelectField
              label={t("geo.communalSection")}
              value={value.sectionGid}
              onChange={(event) => changeSection(event.target.value)}
              disabled={!value.communeGid}
            >
              {entityOptions(sections.data, t("reporting.allSections"))}
            </SelectField>

            <SelectField
              label={t("voter.votingCenter")}
              value={value.votingCenterGid}
              onChange={(event) => setField("votingCenterGid", event.target.value)}
              disabled={!value.sectionGid}
            >
              {entityOptions(votingCenters.data, t("reporting.allVotingCenters"))}
            </SelectField>
          </div>
        </div>
      ) : null}

      <div className={styles.filterActions}>
        {loading ? <CircularProgress size={18} /> : null}
        {advanced ? (
          <Button
            variant="outlined"
            startIcon={<RestartAltOutlinedIcon />}
            onClick={onReset}
          >
            {t("common.reset")}
          </Button>
        ) : null}
        <Button
          variant="contained"
          startIcon={<AutorenewOutlinedIcon />}
          disabled={invalidDateRange}
          onClick={() => onApply(value)}
        >
          {advanced ? t("reporting.applyFilters") : t("reporting.refresh")}
        </Button>
      </div>
    </section>
  );
};

export default ReportingFilters;
